<template>
  <div class="settings-page">
    <!-- 连接服务器设置卡片 -->
    <div class="setting-card">
      <div class="card-header">
        <div class="header-wrapper">
          <h2><i class="fas fa-server"></i> {{ $t('distributionServer.connectionServerSettings') }}</h2>
          <div class="header-actions">
            <el-button type="info" size="small" @click="showLogDialog">
              <el-icon><Document /></el-icon>
              {{ $t('distributionServer.viewLogs') }}
            </el-button>
          </div>
        </div>
      </div>
      <div class="card-content">
        <div class="table-responsive">
          <table class="settings-table">
            <thead>
              <tr>
                <th>{{ $t('distributionServer.address') }}</th>
                <th>{{ $t('distributionServer.port') }}</th>
                <th>{{ $t('distributionServer.key') }}</th>
                <th>{{ $t('distributionServer.status') }}</th>
                <th>{{ $t('distributionServer.operation') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td :data-label="$t('distributionServer.address')">{{ connectionServer.dcServerIP }}</td>
                <td :data-label="$t('distributionServer.port')">{{ connectionServer.dcServerPort }}</td>
                <td :data-label="$t('distributionServer.key')">{{ maskToken(connectionServer.dcServerToken) }}</td>
                <td :data-label="$t('distributionServer.status')">
                  <span class="status-tag" :class="!connectionStatus ? 'enabled' : 'disabled'">
                    {{ !connectionStatus ? $t('distributionServer.enabled') : $t('distributionServer.disabled') }}
                  </span>
                </td>
                <td :data-label="$t('distributionServer.operation')" class="action-cell">
                  <div class="action-buttons">
                    <!-- 编辑按钮始终显示 -->
                    <el-button
                      v-if="connectionStatus"
                      type="primary"
                      size="small"
                      @click="openConnectionEditDialog"
                      :disabled="connectionLoading"
                    >
                      {{ $t('distributionServer.edit') }}
                    </el-button>

                    <!-- 状态相关按钮 -->
                    <template v-if="connectionStatus">
                      <el-button
                        type="success"
                        size="small"
                        @click="confirmAction(openConnectionServer, $t('distributionServer.confirmStart'))"
                        :loading="connectionLoading"
                      >
                        {{ $t('distributionServer.start') }}
                      </el-button>
                    </template>
                    <template v-else>
                      <el-button
                        v-if="!connectionStatus"
                        type="danger"
                        size="small"
                        @click="confirmAction(closeBrServer, $t('distributionServer.confirmStop'))"
                        :loading="connectionLoading"
                      >
                        {{ $t('distributionServer.stop') }}
                      </el-button>
                    </template>

                    <!-- 恢复默认值按钮始终显示 -->
                    <el-button
                      v-if="connectionStatus"
                      type="info"
                      size="small"
                      @click="confirmAction(updateDefaultBrServer, $t('distributionServer.confirmRestore'))"
                      :loading="connectionLoading"
                    >
                      {{ $t('distributionServer.restoreDefaults') }}
                    </el-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 分发服务器设置卡片 -->
    <div class="setting-card">
      <div class="card-header">
        <h2><i class="fas fa-share-alt"></i> {{ $t('distributionServer.distributionServerSettings') }}</h2>
      </div>
      <div class="card-content">
        <div class="table-responsive">
          <table class="settings-table">
            <thead>
              <tr>
                <th>{{ $t('distributionServer.port') }}</th>
                <th>{{ $t('distributionServer.key') }}</th>
                <th>{{ $t('distributionServer.status') }}</th>
                <th>{{ $t('distributionServer.operation') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td :data-label="$t('distributionServer.port')">{{ distributionServer.port }}</td>
                <td :data-label="$t('distributionServer.key')">{{ maskToken(distributionServer.token) }}</td>
                <td :data-label="$t('distributionServer.status')">
                  <span class="status-tag" :class="distributionStatus ? 'enabled' : 'disabled'">
                    {{ distributionStatus ? $t('distributionServer.running') : $t('distributionServer.stopped') }}
                  </span>
                </td>
                <td :data-label="$t('distributionServer.operation')" class="action-cell">
                  <div class="action-buttons">
                    <!-- 编辑按钮始终显示 -->
                    <el-button
                      v-if="!distributionStatus"
                      type="primary"
                      size="small"
                      @click="openDistributionEditDialog"
                      :disabled="distributionLoading"
                    >
                      {{ $t('distributionServer.edit') }}
                    </el-button>

                    <!-- 状态相关按钮 -->
                    <template v-if="!distributionStatus">
                      <el-button
                        v-if="!distributionStatus"
                        type="success"
                        size="small"
                        @click="confirmAction(openDistributionServer, $t('distributionServer.confirmStartDistribution'))"
                        :loading="distributionLoading"
                      >
                        {{ $t('distributionServer.start') }}
                      </el-button>
                    </template>
                    <template v-else>
                      <el-button
                        v-if="distributionStatus"
                        type="danger"
                        size="small"
                        @click="confirmAction(stopBrServerService, $t('distributionServer.confirmStopDistribution'))"
                        :loading="distributionLoading"
                      >
                        停止
                      </el-button>
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 端口映射设置卡片 -->
    <div class="setting-card">
      <div class="card-header">
        <h2><i class="fas fa-network-wired"></i> {{ $t('mapping.title') }}</h2>
        <p class="card-subtitle">
          （{{ $t('distributionServer.portMappingHint') }}）
        </p>
      </div>
      <div class="card-content">
        <div class="table-responsive">
          <table class="settings-table">
            <thead>
              <tr>
                <th>{{ $t('distributionServer.validPortRange') }}</th>
                <th>{{ $t('distributionServer.autoAssignStartPort') }}</th>
                <th>{{ $t('distributionServer.operation') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td :data-label="$t('distributionServer.validPortRange')">{{ portConfig.validPort }}</td>
                <td :data-label="$t('distributionServer.autoAssignStartPort')">{{ portConfig.firstAutoPort }}</td>
                <td :data-label="$t('distributionServer.operation')" class="action-cell">
                  <div class="action-buttons">
                    <el-button
                      type="primary"
                      size="small"
                      @click="openPortEditDialog"
                      :loading="portLoading"
                    >
                      {{ $t('distributionServer.edit') }}
                    </el-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 连接服务器编辑对话框 -->
    <el-dialog
      v-model="connectionDialog.visible"
      :title="$t('distributionServer.editConnectionSettings')"
      :width="isMobile ? '95%' : '500px'"
      @close="resetConnectionForm"
      :destroy-on-close="true"
      class="settings-dialog"
    >
      <el-form
        ref="connectionFormRef"
        :model="connectionDialog.form"
        :rules="connectionRules"
        :label-position="isMobile ? 'top' : 'right'"
        label-width="100px"
      >
        <el-form-item label="IP" prop="dcServerIP">
          <el-input v-model="connectionDialog.form.dcServerIP" :placeholder="$t('common.required')" />
        </el-form-item>
        <el-form-item :label="$t('distributionServer.port')" prop="dcServerPort">
          <el-input-number
            v-model="connectionDialog.form.dcServerPort"
            :min="1"
            :max="65535"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item :label="$t('distributionServer.key')" prop="dcServerToken">
          <el-input
            v-model="connectionDialog.form.dcServerToken"
            show-password
            placeholder="10-20位密钥"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="connectionDialog.visible = false">{{ $t('common.cancel') }}</el-button>
          <el-button
            type="primary"
            @click="submitConnectionEdit"
            :loading="connectionLoading"
          >
            {{ $t('common.confirm') }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 分发服务器编辑对话框 -->
    <el-dialog
      v-model="distributionDialog.visible"
      :title="$t('distributionServer.editDistributionSettings')"
      :width="isMobile ? '95%' : '500px'"
      @close="resetDistributionForm"
      :destroy-on-close="true"
      class="settings-dialog"
    >
      <el-form
        ref="distributionFormRef"
        :model="distributionDialog.form"
        :rules="distributionRules"
        :label-position="isMobile ? 'top' : 'right'"
        label-width="100px"
      >
        <el-form-item :label="$t('distributionServer.port')" prop="port">
          <el-input-number
            v-model="distributionDialog.form.port"
            :min="1"
            :max="65535"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item :label="$t('distributionServer.key')" prop="token">
          <el-input
            v-model="distributionDialog.form.token"
            show-password
            placeholder="10-20位密钥"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="distributionDialog.visible = false">{{ $t('common.cancel') }}</el-button>
          <el-button
            type="primary"
            @click="submitDistributionEdit"
            :loading="distributionLoading"
          >
            {{ $t('common.confirm') }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 端口映射编辑对话框 -->
    <el-dialog
      v-model="portDialog.visible"
      :title="$t('distributionServer.editPortMappingSettings')"
      :width="isMobile ? '95%' : '500px'"
      @close="resetPortForm"
      :destroy-on-close="true"
      class="settings-dialog"
    >
      <el-form
        ref="portFormRef"
        :model="portDialog.form"
        :rules="portRules"
        :label-position="isMobile ? 'top' : 'right'"
        label-width="140px"
      >
        <el-form-item :label="$t('distributionServer.validPortRange')" prop="validPort">
          <el-input
            v-model="portDialog.form.validPort"
            placeholder="例如: 80,1000-1100,2000"
          />
        </el-form-item>
        <el-form-item :label="$t('distributionServer.autoAssignStartPort')" prop="firstAutoPort">
          <el-input-number
            v-model="portDialog.form.firstAutoPort"
            :min="1"
            :max="65535"
            style="width: 100%"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="portDialog.visible = false">{{ $t('common.cancel') }}</el-button>
          <el-button
            type="primary"
            @click="submitPortEdit"
            :loading="portLoading"
          >
            {{ $t('common.confirm') }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 系统设置日志查看对话框 -->
    <el-dialog
      v-model="logDialogVisible"
      :title="$t('log.title')"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog log-dialog"
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>{{ $t('log.logTotal', { count: logTotal }) }}</span>
          <div class="auto-refresh-control">
            <span class="refresh-label">{{ $t('log.autoRefresh') }}</span>
            <el-switch
              v-model="autoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="autoRefreshEnabled">(5{{ $t('log.second') }})</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button
            type="success"
            size="small"
            @click="downloadAllLogs"
            :loading="logDownloading"
            :icon="Download"
          >
            {{ $t('log.downloadAllLogs') }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshLogs"
            :loading="logLoading"
            :icon="Refresh"
          >
            {{ $t('common.refresh') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="clearLogs"
            :loading="logClearing"
          >
            {{ $t('log.clearLogs') }}
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="logContentRef">
        <div v-if="logsData.length === 0" class="empty-log">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('log.noLogs') }}</p>
        </div>
        <div v-else class="log-list">
          <div 
            v-for="(log, index) in logsData" 
            :key="index" 
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="logTotal > 0">
        <el-pagination
          v-model:current-page="logCurrentPage"
          v-model:page-size="logPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="logTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLogSizeChange"
          @current-change="handleLogPageChange"
          small
        />
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="logDialogVisible = false">关闭</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { API_BASE_URL } from '@/config';
import { Document, InfoFilled } from '@element-plus/icons-vue';

export default {
  name: 'SettingsPage',
  components: { Document, InfoFilled },
  setup() {
    const router = useRouter();
    const isMobile = ref(false);
    
    // 检测移动端
    const checkMobile = () => {
      isMobile.value = window.innerWidth < 768;
    };
    
    // 加载状态
    const loading = ref(true);
    const portLoading = ref(false);
    const connectionLoading = ref(false);
    const distributionLoading = ref(false);

    // 表单引用
    const connectionFormRef = ref(null);
    const distributionFormRef = ref(null);
    const portFormRef = ref(null);

    // 自动刷新定时器
    let refreshTimer = null;
    let logRefreshInterval = ref(null);

    // 日志对话框相关
    const logDialogVisible = ref(false);
    const logLoading = ref(false);
    const logClearing = ref(false);
    const logDownloading = ref(false);
    const logsData = ref([]);
    const logTotal = ref(0);
    const logCurrentPage = ref(1);
    const logPageSize = ref(50);
    const logContentRef = ref(null);
    const autoRefreshEnabled = ref(true); // 默认开启自动刷新

    // 端口映射设置相关状态
    const portConfig = ref({
      validPort: '',
      firstAutoPort: 10000
    });

    // 连接服务器设置相关状态
    const connectionServer = ref({
      dcServerIP: '',
      dcServerPort: 0,
      dcServerToken: ''
    });
    const connectionStatus = ref(false);

    // 分发服务器设置相关状态
    const distributionServer = ref({
      port: 0,
      token: ''
    });
    const distributionStatus = ref(false);

    // 对话框状态
    const connectionDialog = ref({
      visible: false,
      form: {
        dcServerIP: '',
        dcServerPort: 0,
        dcServerToken: ''
      }
    });

    const distributionDialog = ref({
      visible: false,
      form: {
        port: 0,
        token: ''
      }
    });

    const portDialog = ref({
      visible: false,
      form: {
        validPort: '',
        firstAutoPort: 10000
      }
    });

    // 表单验证规则
    const connectionRules = {
      dcServerIP: [
        { required: true, message: '请输入IP地址', trigger: 'blur' },
        { validator: (rule, value, callback) => {
          if (!validateIP(value)) {
            callback(new Error('IP地址格式不正确'));
          } else {
            callback();
          }
        }, trigger: 'blur' }
      ],
      dcServerPort: [
        { required: true, message: '请输入端口号', trigger: 'blur' },
        { type: 'number', min: 1, max: 65535, message: '端口取值范围是1-65535', trigger: 'blur' }
      ],
      dcServerToken: [
        { required: true, message: '请输入密钥', trigger: 'blur' },
        { min: 10, max: 20, message: '密钥长度为10-20位', trigger: 'blur' }
      ]
    };

    const distributionRules = {
      port: [
        { required: true, message: '请输入端口号', trigger: 'blur' },
        { type: 'number', min: 1, max: 65535, message: '端口取值范围是1-65535', trigger: 'blur' }
      ],
      token: [
        { required: true, message: '请输入密钥', trigger: 'blur' },
        { min: 10, max: 20, message: '密钥长度为10-20位', trigger: 'blur' }
      ]
    };

    const portRules = {
      validPort: [
        { required: true, message: '请输入有效端口号范围', trigger: 'blur' },
        { validator: (rule, value, callback) => {
          if (!validatePortRange(value)) {
            callback(new Error('端口范围格式不正确，请使用如: 80,1000-1100,2000 的格式'));
          } else {
            callback();
          }
        }, trigger: 'blur' }
      ],
      firstAutoPort: [
        { required: true, message: '请输入起始端口号', trigger: 'blur' },
        { type: 'number', min: 1, max: 65535, message: '起始端口号取值范围是1-65535', trigger: 'blur' }
      ]
    };

    // 确认操作对话框
    const confirmAction = (action, message) => {
      ElMessageBox.confirm(message, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
      .then(() => {
        action();
      })
      .catch(() => {
        ElMessage.info('已取消操作');
      });
    };

    // 验证IP地址格式
    const validateIP = (ip) => {
      const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      return ipRegex.test(ip) || ip === 'localhost';
    };

    // 验证端口范围格式
    const validatePortRange = (range) => {
      if (!range) return false;
      const parts = range.split(',');
      const portRegex = /^(\d+)(-\d+)?$/;
      
      for (const part of parts) {
        if (!portRegex.test(part.trim())) {
          return false;
        }
      }
      return true;
    };

    // 掩码显示token
    const maskToken = (token) => {
      if (!token) return '';
      return '•'.repeat(Math.min(token.length, 10));
    };

    // 打开连接服务器编辑对话框
    const openConnectionEditDialog = () => {
      connectionDialog.value.form = {
        dcServerIP: connectionServer.value.dcServerIP,
        dcServerPort: connectionServer.value.dcServerPort,
        dcServerToken: connectionServer.value.dcServerToken
      };
      connectionDialog.value.visible = true;
    };

    // 打开连接服务器
    const openConnectionServer = async () => {
      try {
        connectionLoading.value = true;
        
        const params = new URLSearchParams();
        params.append('DCServerIP', connectionServer.value.dcServerIP);
        params.append('DCServerPort', connectionServer.value.dcServerPort);
        params.append('DCServerToken', connectionServer.value.dcServerToken);
        
        const response = await fetch(`${API_BASE_URL}/update-brServer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        });
        
        const data = await response.json();
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '开启连接服务器失败');
        }
        
        await fetchSettings();
        ElMessage.success(data.errMsg || '连接服务器已开启');
      } catch (error) {
        console.error('开启连接服务器失败:', error);
        ElMessage.error(error.message || '开启连接服务器失败');
      } finally {
        connectionLoading.value = false;
      }
    };

    // 打开分发服务器编辑对话框
    const openDistributionEditDialog = () => {
      distributionDialog.value.form = {
        port: distributionServer.value.port,
        token: distributionServer.value.token
      };
      distributionDialog.value.visible = true;
    };

    // 打开分发服务器
    const openDistributionServer = async () => {
      try {
        distributionLoading.value = true;
        
        const params = new URLSearchParams();
        params.append('BrServerServicePort', distributionServer.value.port);
        params.append('BrServerServiceToken', distributionServer.value.token);
        
        const response = await fetch(`${API_BASE_URL}/update-brServerService`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        });
        
        const data = await response.json();
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '开启分发服务器失败');
        }
        
        await fetchSettings();  
        ElMessage.success(data.errMsg || '分发服务器已开启');
      } catch (error) {
        console.error('开启分发服务器失败:', error);
        ElMessage.error(error.message || '开启分发服务器失败');
      } finally {
        distributionLoading.value = false;
      }
    };

    // 打开端口映射编辑对话框
    const openPortEditDialog = () => {
      portDialog.value.form = {
        validPort: portConfig.value.validPort,
        firstAutoPort: portConfig.value.firstAutoPort
      };
      portDialog.value.visible = true;
    };

    // 重置连接表单
    const resetConnectionForm = () => {
      if (connectionFormRef.value) {
        connectionFormRef.value.clearValidate();
      }
    };

    // 重置分发表单
    const resetDistributionForm = () => {
      if (distributionFormRef.value) {
        distributionFormRef.value.clearValidate();
      }
    };

    // 重置端口表单
    const resetPortForm = () => {
      if (portFormRef.value) {
        portFormRef.value.clearValidate();
      }
    };

    // 提交连接服务器编辑
    const submitConnectionEdit = async () => {
      if (!connectionFormRef.value) return;
      
      await connectionFormRef.value.validate(async (valid) => {
        if (valid) {
          // 更新配置但不开启
          try {
            connectionLoading.value = true;

            const params = new URLSearchParams();
            params.append('DCServerIP', connectionDialog.value.form.dcServerIP);
            params.append('DCServerPort', connectionDialog.value.form.dcServerPort);
            params.append('DCServerToken', connectionDialog.value.form.dcServerToken);
            
            const response = await fetch(`${API_BASE_URL}/update-brServer`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              credentials: 'include',
              body: params
            });
            
            const data = await response.json();
            
            if (data.errCode !== 0) {
              throw new Error(data.errMsg || '更新连接服务器配置失败');
            }
            
            await fetchSettings();
            ElMessage.success(data.errMsg || '连接服务器配置已更新');
            connectionDialog.value.visible = false;
          } catch (error) {
            console.error('更新连接服务器配置失败:', error);
            ElMessage.error(error.message || '更新连接服务器配置失败');
          } finally {
            connectionLoading.value = false;
          }
        }
      });
    };

    // 提交分发服务器编辑
    const submitDistributionEdit = async () => {
      if (!distributionFormRef.value) return;
      
      await distributionFormRef.value.validate(async (valid) => {
        if (valid) {
          // 更新配置但不开启
          try {
            distributionLoading.value = true;

            const params = new URLSearchParams();
            params.append('BrServerServicePort', distributionDialog.value.form.port);
            params.append('BrServerServiceToken', distributionDialog.value.form.token);
            
            const response = await fetch(`${API_BASE_URL}/update-brServerService`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              credentials: 'include',
              body: params
            });
            
            const data = await response.json();
            
            if (data.errCode !== 0) {
              throw new Error(data.errMsg || '更新分发服务器配置失败');
            }
            
            await fetchSettings();  
            ElMessage.success(data.errMsg || '分发服务器配置已更新');
            distributionDialog.value.visible = false;
          } catch (error) {
            console.error('更新分发服务器配置失败:', error);
            ElMessage.error(error.message || '更新分发服务器配置失败');
          } finally {
            distributionLoading.value = false;
          }
        }
      });
    };

    // 提交端口映射编辑
    const submitPortEdit = async () => {
      if (!portFormRef.value) return;
      
      await portFormRef.value.validate(async (valid) => {
        if (valid) {
          try {
            portLoading.value = true;

            const params = new URLSearchParams();
            params.append('firstAutoPort', portDialog.value.form.firstAutoPort);
            params.append('validPort', portDialog.value.form.validPort);
            
            const response = await fetch(`${API_BASE_URL}/update-portConfig`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              credentials: 'include',
              body: params
            });
            
            const data = await response.json();
            
            if (data.errCode !== 0) {
              throw new Error(data.errMsg || '更新端口映射配置失败');
            }
            
            await fetchSettings();
            ElMessage.success(data.errMsg || '端口映射配置已更新');
            portDialog.value.visible = false;
          } catch (error) {
            console.error('更新端口映射配置失败:', error);
            ElMessage.error(error.message || '更新端口映射配置失败');
          } finally {
            portLoading.value = false;
          }
        }
      });
    };

    // 获取设置信息
    const fetchSettings = async () => {
      try {
        loading.value = true;
        const response = await fetch(`${API_BASE_URL}/get-settingInfo`, {
          credentials: 'include'
        });
        const data = await response.json();

        if (data.errCode === 1000) {
          ElMessage.warning('请先登录');
          router.push('/user-login?callback=distribution-server');
          return;
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '获取设置信息失败');
        }

        // 端口映射设置
        portConfig.value.validPort = data.ServerConfig.validPort;
        portConfig.value.firstAutoPort = data.ServerConfig.firstAutoPort;

        // 连接服务器设置
        connectionServer.value.dcServerIP = data.DCServerIP;
        connectionServer.value.dcServerPort = data.DCServerPort;
        connectionServer.value.dcServerToken = data.DCServerToken;
        connectionStatus.value = data.selectSrvStatus;

        // 分发服务器设置
        distributionServer.value.port = data.BRServer.port;
        distributionServer.value.token = data.BRServer.token; 
        distributionStatus.value = data.BRServerStatus;

      } catch (error) {
        console.error('获取设置信息失败:', error);
        ElMessage.error(error.message || '获取设置信息失败');
      } finally {
        loading.value = false;
      }
    };

    // 恢复默认连接服务器配置
    const updateDefaultBrServer = async () => {
      try {
        connectionLoading.value = true;
        
        const response = await fetch(`${API_BASE_URL}/update-defaultBrServer`, {
          method: 'POST',
          credentials: 'include'
        });
        
        const data = await response.json();
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '恢复默认连接服务器配置失败');
        }

        await fetchSettings();        
        ElMessage.success(data.errMsg || '已恢复默认连接服务器配置');
      } catch (error) {
        console.error('恢复默认连接服务器配置失败:', error);
        ElMessage.error(error.message || '恢复默认连接服务器配置失败');
      } finally {
        connectionLoading.value = false;
      }
    };

    // 关闭连接服务器
    const closeBrServer = async () => {
      try {
        connectionLoading.value = true;
        
        const response = await fetch(`${API_BASE_URL}/close-brServer`, {
          method: 'POST',
          credentials: 'include'
        });
        
        const data = await response.json();
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '关闭连接服务器失败');
        }
        
        await fetchSettings();
        ElMessage.success(data.errMsg || '连接服务器已关闭');
      } catch (error) {
        console.error('关闭连接服务器失败:', error);
        ElMessage.error(error.message || '关闭连接服务器失败');
      } finally {
        connectionLoading.value = false;
      }
    };

    // 停止分发服务器
    const stopBrServerService = async () => {
      try {
        distributionLoading.value = true;
        
        const response = await fetch(`${API_BASE_URL}/stop-brServerService`, {
          method: 'POST',
          credentials: 'include'
        });
        
        const data = await response.json();
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '停止分发服务器失败');
        }
        
        await fetchSettings();
        ElMessage.success(data.errMsg || '分发服务器已停止');
      } catch (error) {
        console.error('停止分发服务器失败:', error);
        ElMessage.error(error.message || '停止分发服务器失败');
      } finally {
        distributionLoading.value = false;
      }
    };

    // 显示日志对话框
    const showLogDialog = () => {
      logDialogVisible.value = true
      fetchLogs()
      // 开启日志自动刷新（如果开启状态）
      if (autoRefreshEnabled.value) {
        startLogAutoRefresh()
      }
    }

    // 切换自动刷新
    const toggleAutoRefresh = (enabled) => {
      if (enabled) {
        startLogAutoRefresh()
        ElMessage.success('已开启自动刷新（5秒/次）')
      } else {
        stopLogAutoRefresh()
        ElMessage.info('已关闭自动刷新')
      }
    }

    // 启动日志自动刷新
    const startLogAutoRefresh = () => {
      stopLogAutoRefresh() // 先停止已有的定时器
      logRefreshInterval.value = setInterval(() => {
        if (logDialogVisible.value) {
          fetchLogs(false) // 静默刷新，不显示加载状态
        }
      }, 5000)
    }

    // 停止日志自动刷新
    const stopLogAutoRefresh = () => {
      if (logRefreshInterval.value) {
        clearInterval(logRefreshInterval.value)
        logRefreshInterval.value = null
      }
    }

    // 获取日志
    const fetchLogs = async (showLoading = true) => {
      try {
        if (showLoading) {
          logLoading.value = true
        }
        
        const params = new URLSearchParams({
          module: 'distributionserver',
          page: logCurrentPage.value,
          size: logPageSize.value
        })
        
        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })
        
        if (!response.ok) throw new Error('网络请求失败')
        
        const data = await response.json()
        
        if (data.errCode === 1000) {
          ElMessage.warning('请先登录')
          router.push('/user-login?callback=distribution-server')
          return
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '获取日志失败')
        }
        
        logsData.value = data.data || []
        logTotal.value = data.count || 0
        
        // 只有在非自动刷新（即手动操作）时才滚动到顶部
        if (showLoading && logContentRef.value) {
          logContentRef.value.scrollTop = 0
        }
      } catch (error) {
        console.error('获取日志失败:', error)
        if (showLoading) {
          ElMessage.error(`获取日志失败: ${error.message || '未知错误'}`)
        }
      } finally {
        if (showLoading) {
          logLoading.value = false
        }
      }
    }

    // 刷新日志
    const refreshLogs = () => {
      fetchLogs(true)
    }

    // 清空日志
    const clearLogs = async () => {
      try {
        await ElMessageBox.confirm('确定要清空所有系统设置日志吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        logClearing.value = true
        
        const params = new URLSearchParams({
          module: 'distributionserver'
        })
        
        const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
          method: 'DELETE',
          credentials: 'include'
        })
        
        if (!response.ok) throw new Error('网络请求失败')
        
        const data = await response.json()
        
        if (data.errCode === 1000) {
          ElMessage.warning('请先登录')
          router.push('/user-login?callback=distribution-server')
          return
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '清空日志失败')
        }
        
        ElMessage.success('日志已清空')
        fetchLogs(true)
      } catch (error) {
        if (error !== 'cancel') {
          console.error('清空日志失败:', error)
          ElMessage.error(`清空日志失败: ${error.message || '未知错误'}`)
        }
      } finally {
        logClearing.value = false
      }
    }

    // 下载所有日志
    const downloadAllLogs = async () => {
      try {
        logDownloading.value = true
        
        // 获取所有日志（设置一个足够大的size值）
        const params = new URLSearchParams({
          module: 'distributionserver',
          page: 1,
          size: 10000 // 设置一个较大的数值，获取尽可能多的日志
        })
        
        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })
        
        if (!response.ok) throw new Error('网络请求失败')
        
        const data = await response.json()
        
        if (data.errCode === 1000) {
          ElMessage.warning('请先登录')
          router.push('/user-login?callback=distribution-server')
          return
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || '获取日志失败')
        }
        
        const allLogs = data.data || []
        
        if (allLogs.length === 0) {
          ElMessage.warning('暂无日志可下载')
          return
        }
        
        // 格式化所有日志内容
        let logContent = ''
        allLogs.forEach(log => {
          logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`
        })
        
        // 创建下载链接
        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `system_setting_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        
        ElMessage.success(`所有日志下载成功，共 ${allLogs.length} 条记录`)
      } catch (error) {
        console.error('下载日志失败:', error)
        ElMessage.error(`下载日志失败: ${error.message || '未知错误'}`)
      } finally {
        logDownloading.value = false
      }
    }

    // 处理每页条数变化
    const handleLogSizeChange = (size) => {
      logPageSize.value = size
      logCurrentPage.value = 1
      fetchLogs(true)
    }

    // 处理页码变化
    const handleLogPageChange = (page) => {
      logCurrentPage.value = page
      fetchLogs(true)
    }

    // 启动自动刷新
    const startAutoRefresh = () => {
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
      refreshTimer = setInterval(() => {
        fetchSettings();
      }, 5000); // 5秒刷新一次
    };

    // 窗口大小变化处理
    const handleResize = () => {
      checkMobile();
    };

    onMounted(() => {
      checkMobile();
      window.addEventListener('resize', handleResize);
      fetchSettings();
      startAutoRefresh();
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
      stopLogAutoRefresh();
    });

    return {
      isMobile,
      loading,
      portConfig,
      portLoading,
      connectionServer,
      connectionStatus,
      connectionLoading,
      distributionServer,
      distributionStatus,
      distributionLoading,
      connectionDialog,
      distributionDialog,
      portDialog,
      connectionFormRef,
      distributionFormRef,
      portFormRef,
      connectionRules,
      distributionRules,
      portRules,
      maskToken,
      openConnectionEditDialog,
      openConnectionServer,
      openDistributionEditDialog,
      openDistributionServer,
      openPortEditDialog,
      submitConnectionEdit,
      submitDistributionEdit,
      submitPortEdit,
      resetConnectionForm,
      resetDistributionForm,
      resetPortForm,
      updateDefaultBrServer,
      closeBrServer,
      stopBrServerService,
      confirmAction,
      // 日志相关
      logDialogVisible,
      logLoading,
      logClearing,
      logDownloading,
      logsData,
      logTotal,
      logCurrentPage,
      logPageSize,
      logContentRef,
      autoRefreshEnabled,
      showLogDialog,
      toggleAutoRefresh,
      refreshLogs,
      clearLogs,
      downloadAllLogs,
      handleLogSizeChange,
      handleLogPageChange
    };
  }
};
</script>

<style scoped>
.settings-page {
  padding: 0px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  background-color: #f5f7fa;
  min-height: 100vh;
}

@media (max-width: 768px) {
  .settings-page {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .settings-page {
    padding: 8px;
  }
}

.setting-card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
  transition: all 0.3s ease;
  width: 100%;
  box-sizing: border-box;
}

.setting-card:hover {
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.15);
}

.card-header {
  background: linear-gradient(90deg, #f0f2f5 0%, #e6e9ef 100%);
  padding: 16px 20px;
  border-bottom: 1px solid #e6e6e6;
}

.header-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.card-header h2 {
  margin: 0;
  font-size: 16px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-header i {
  font-size: 18px;
  color: #409EFF;
}

.card-subtitle {
  font-size: 14px;
  color: #606266;
  margin: 5px 0 0 0;
}

.card-content {
  padding: 20px;
}

@media (max-width: 768px) {
  .setting-card {
    margin-bottom: 16px;
  }
  
  .card-header {
    padding: 12px 16px;
  }
  
  .card-header h2 {
    font-size: 15px;
  }
  
  .card-header i {
    font-size: 16px;
  }
  
  .card-subtitle {
    font-size: 12px;
  }
  
  .card-content {
    padding: 16px;
  }
  
  .header-wrapper {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 480px) {
  .card-header {
    padding: 10px 12px;
  }
  
  .card-content {
    padding: 12px;
  }
}

.table-responsive {
  overflow-x: auto;
  width: 100%;
}

@media (max-width: 768px) {
  .table-responsive {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.settings-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.settings-table th,
.settings-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #ebeef5;
}

.settings-table th {
  background-color: #f5f7fa;
  color: #909399;
  font-weight: 600;
  white-space: nowrap;
}

.settings-table td {
  color: #606266;
  vertical-align: middle;
}

.settings-table tr:hover td {
  background-color: #f5f7fa;
}

@media (max-width: 768px) {
  .settings-table {
    width: 100%;
    min-width: 100%;
    display: block;
  }
  
  .settings-table thead {
    display: none;
  }
  
  .settings-table tbody,
  .settings-table tr {
    width: 100%;
    display: block;
  }
  
  .settings-table tr {
    margin-bottom: 16px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background: #fff;
  }
  
  .settings-table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    border-bottom: 1px solid #ebeef5;
    width: 100%;
    box-sizing: border-box;
  }
  
  .settings-table td:before {
    content: attr(data-label);
    font-weight: 600;
    color: #909399;
    margin-right: 12px;
    flex-shrink: 0;
    min-width: 100px;
    font-size: 13px;
  }
  
  .settings-table td:last-child {
    border-bottom: none;
  }
  
  .settings-table td.action-cell {
    padding: 10px 12px;
  }
  
  .settings-table td.action-cell:before {
    align-self: flex-start;
    padding-top: 4px;
  }
}

@media (max-width: 480px) {
  .settings-table td {
    padding: 8px 10px;
    font-size: 12px;
  }
  
  .settings-table td:before {
    min-width: 80px;
    font-size: 12px;
  }
}

.status-tag {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-tag.enabled {
  background-color: #f0f9eb;
  color: #67c23a;
}

.status-tag.disabled {
  background-color: #f5f5f5;
  color: #909399;
}

.action-cell .action-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: nowrap;
}

.action-cell .action-buttons .el-button {
  flex-shrink: 0;
}

.action-cell .action-buttons .el-button + .el-button {
  margin-left: 0;
}

@media (max-width: 768px) {
  .action-cell .action-buttons {
    justify-content: flex-end;
  }
  
  .action-cell .el-button--small {
    padding: 4px 8px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .action-cell .action-buttons {
    gap: 2px;
  }
  
  .action-cell .el-button--small {
    padding: 4px 6px;
    font-size: 11px;
  }
}

/* 日志对话框样式 */
.log-dialog :deep(.el-dialog__body) {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.log-toolbar .log-stats {
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: 13px;
  color: #606266;
}

.log-toolbar .log-stats .auto-refresh-control {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 16px;
  border-left: 1px solid #dcdfe6;
}

.log-toolbar .log-stats .auto-refresh-control .refresh-label {
  font-size: 12px;
  color: #909399;
}

.log-toolbar .log-stats .auto-refresh-control .refresh-interval {
  font-size: 12px;
  color: #67c23a;
}

.log-toolbar .log-actions {
  display: flex;
  gap: 8px;
}

@media (max-width: 768px) {
  .log-toolbar {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .log-toolbar .log-stats {
    width: 100%;
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .log-toolbar .log-stats .auto-refresh-control {
    padding-left: 0;
    border-left: none;
  }
  
  .log-toolbar .log-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 480px) {
  .log-toolbar .log-stats {
    font-size: 12px;
  }
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.log-content .empty-log {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
}

.log-content .empty-log .el-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.log-content .empty-log p {
  margin: 0;
  font-size: 14px;
}

.log-content .log-list {
  padding: 12px;
}

.log-content .log-list .log-item {
  padding: 8px 12px;
  border-bottom: 1px solid #e4e7ed;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.log-content .log-list .log-item:last-child {
  border-bottom: none;
}

.log-content .log-list .log-item.log-level-error {
  background-color: #fef0f0;
}

.log-content .log-list .log-item.log-level-warn {
  background-color: #fdf6ec;
}

.log-content .log-list .log-item .log-time {
  color: #909399;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-content .log-list .log-item .log-level {
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-content .log-list .log-item .log-level.level-error {
  color: #f56c6c;
}

.log-content .log-list .log-item .log-level.level-warn {
  color: #e6a23c;
}

.log-content .log-list .log-item .log-level.level-info {
  color: #409eff;
}

.log-content .log-list .log-item .log-message {
  color: #303133;
  word-break: break-all;
  flex: 1;
}

@media (max-width: 768px) {
  .log-content {
    max-height: 350px;
  }
  
  .log-content .log-list {
    padding: 8px;
  }
  
  .log-content .log-list .log-item {
    font-size: 12px;
    flex-wrap: wrap;
    gap: 4px;
  }
  
  .log-content .log-list .log-item .log-time {
    width: 100%;
    margin-bottom: 2px;
  }
  
  .log-content .log-list .log-item .log-message {
    width: 100%;
    margin-left: 0;
  }
}

@media (max-width: 480px) {
  .log-content {
    max-height: 300px;
  }
  
  .log-content .log-list .log-item {
    font-size: 11px;
  }
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .log-pagination {
    justify-content: center;
  }
}

:deep(.el-dialog__body) {
  padding-top: 20px;
  padding-bottom: 10px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

@media (max-width: 480px) {
  .dialog-footer {
    flex-direction: column;
  }
  
  .dialog-footer .el-button {
    width: 100%;
    margin-left: 0 !important;
  }
}

/* Element Plus 对话框移动端适配 */
.settings-dialog :deep(.el-dialog) {
  margin: 0 auto;
}

@media (max-width: 768px) {
  .settings-dialog :deep(.el-dialog) {
    width: 95% !important;
    margin-top: 5vh !important;
  }
}

@media (max-width: 480px) {
  .settings-dialog :deep(.el-dialog) {
    width: 100% !important;
    height: auto !important;
    margin: 0 !important;
    max-height: 100%;
    border-radius: 0;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin-bottom: 0 !important;
  }
  
  .settings-dialog :deep(.el-dialog__header) {
    padding: 12px 16px;
    border-bottom: 1px solid #ebeef5;
  }
  
  .settings-dialog :deep(.el-dialog__body) {
    padding: 16px;
    max-height: calc(80vh - 120px);
    overflow-y: auto;
  }
  
  .settings-dialog :deep(.el-dialog__footer) {
    padding: 12px 16px;
    border-top: 1px solid #ebeef5;
  }
}

/* Element Plus 组件适配 */
:deep(.el-input__inner) {
  width: 100%;
}

@media (max-width: 768px) {
  :deep(.el-input__inner) {
    font-size: 13px;
    height: 32px;
    line-height: 32px;
  }
  
  :deep(.el-input-number) {
    width: 100% !important;
  }
  
  :deep(.el-input-number .el-input__inner) {
    text-align: left;
  }
}

@media (max-width: 480px) {
  :deep(.el-input__inner) {
    font-size: 12px;
    height: 30px;
    line-height: 30px;
  }
}

:deep(.el-form-item__label) {
  font-weight: 500;
}

@media (max-width: 768px) {
  :deep(.el-form-item__label) {
    font-size: 13px;
    padding-bottom: 4px;
  }
}

@media (max-width: 480px) {
  :deep(.el-form-item__label) {
    font-size: 12px;
  }
}

:deep(.el-button--small) {
  padding: 6px 12px;
}

@media (max-width: 768px) {
  :deep(.el-button--small) {
    padding: 6px 10px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  :deep(.el-button--small) {
    padding: 5px 8px;
    font-size: 11px;
  }
}

:deep(.el-switch) {
  @media (max-width: 768px) {
    .el-switch__core {
      width: 36px !important;
      height: 18px !important;
      
      &:after {
        width: 14px !important;
        height: 14px !important;
      }
    }
  }
}
</style>